import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { projectService } from '../../services/projectService';
import { mockSubjects, mockUsers } from '../../data/mockData';
import { dateUtils } from '../../utils';
import LoadingSpinner from '../common/LoadingSpinner';

// Componentes de secciones
import ProjectHeader from './ProjectHeader';
import SubjectAssignment from './SubjectAssignment';
import ProjectEvaluations from './ProjectEvaluations';
import ProjectResources from './ProjectResources';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useApp();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await projectService.getProjectById(id);
      if (response.success) {
        setProject(response.data);
      } else {
        showError('Proyecto no encontrado');
        navigate('/projects');
      }
    } catch (error) {
      showError('Error al cargar el proyecto');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProject = async (updates) => {
    try {
      setSaving(true);
      const response = await projectService.updateProject(id, updates);
      if (response.success) {
        setProject(response.data);
        showSuccess('Proyecto actualizado exitosamente');
      } else {
        showError(response.message || 'Error al actualizar el proyecto');
      }
    } catch (error) {
      showError('Error al actualizar el proyecto');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'planificado': { color: 'bg-gray-100 text-gray-800', label: 'Planificado' },
      'activo': { color: 'bg-green-100 text-green-800', label: 'Activo' },
      'pausado': { color: 'bg-yellow-100 text-yellow-800', label: 'Pausado' },
      'completado': { color: 'bg-blue-100 text-blue-800', label: 'Completado' },
      'cancelado': { color: 'bg-red-100 text-red-800', label: 'Cancelado' }
    };

    const config = statusConfig[status] || statusConfig['planificado'];
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const tabs = [
    { id: 'overview', name: 'Resumen', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { id: 'subjects', name: 'Asignaturas', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
    { id: 'evaluations', name: 'Evaluaciones', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
    { id: 'resources', name: 'Recursos', icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' }
  ];

  if (loading) {
    return <LoadingSpinner fullScreen message="Cargando proyecto..." />;
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Proyecto no encontrado</h3>
        <button
          onClick={() => navigate('/projects')}
          className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg"
        >
          Volver a Proyectos
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con título y botón volver */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <button
            onClick={() => navigate('/projects')}
            className="hover:text-gray-700 transition-colors"
          >
            Proyectos
          </button>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-gray-900 font-medium text-lg">{project.title}</span>
        </div>
        <button
          onClick={() => navigate('/projects')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>Volver</span>
        </button>
      </div>

      {/* Tabs de Navegación */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Contenido de las Tabs */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Información General */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Información General</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Estado:</span>
                      <div className="mt-1">{getStatusBadge(project.status)}</div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Descripción:</span>
                      <p className="text-gray-900 mt-1">{project.description}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Cursos:</span>
                      <p className="text-gray-900 mt-1">{project.courses.join(', ')}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Inicio:</span>
                        <p className="text-gray-900 mt-1">{dateUtils.formatDate(project.startDate)}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Fin:</span>
                        <p className="text-gray-900 mt-1">{dateUtils.formatDate(project.endDate)}</p>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Creado:</span>
                      <p className="text-gray-900 mt-1">{dateUtils.formatDate(project.createdAt, 'dd/MM/yyyy HH:mm')}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Actualizado:</span>
                      <p className="text-gray-900 mt-1">{dateUtils.formatDate(project.updatedAt, 'dd/MM/yyyy HH:mm')}</p>
                    </div>
                  </div>
                </div>

                {/* Progreso del Proyecto */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Progreso del Proyecto</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">Progreso General</span>
                        <span className="text-sm text-gray-600">{project.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-primary-500 h-3 rounded-full transition-all duration-300"
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900">{project.students || 0}</div>
                        <div className="text-sm text-gray-600">Estudiantes</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900">{project.teachers?.length || 0}</div>
                        <div className="text-sm text-gray-600">Profesores</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-blue-900">{project.subjects?.length || 0}</div>
                        <div className="text-sm text-blue-700">Materias</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-900">
                          {project.subjects?.reduce((acc, subject) => acc + (subject.objectives?.length || 0), 0) || 0}
                        </div>
                        <div className="text-sm text-green-700">Objetivos</div>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-orange-900">{project.deliverables?.length || 0}</div>
                        <div className="text-sm text-orange-700">Entregables</div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-purple-900">{project.resources?.length || 0}</div>
                        <div className="text-sm text-purple-700">Recursos</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'subjects' && (
            <SubjectAssignment
              project={project}
              subjects={mockSubjects}
              onUpdate={handleUpdateProject}
            />
          )}

          {activeTab === 'evaluations' && (
            <ProjectEvaluations
              project={project}
              onUpdate={handleUpdateProject}
            />
          )}

          {activeTab === 'resources' && (
            <ProjectResources
              project={project}
              onUpdate={handleUpdateProject}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;